<template>
	<div class="row">
		<!-- Le document reste sous les yeux pendant la correction : une ligne ne se vérifie qu'en la
		     relisant sur la facture. Il passe au-dessus du tableau sur les écrans étroits, où les deux
		     ne tiennent pas côte à côte. -->
		<div class="col-12 col-xl-5">
			<div class="box invoice-supplier-details__document">
				<iframe v-if="is_pdf" :src="invoice.suppliersocr_pdf_url" class="invoice-supplier-fiche__preview"></iframe>
				<img v-else-if="invoice.suppliersocr_pdf_url" :src="invoice.suppliersocr_pdf_url" class="w-100" />
				<p v-else class="mb-0">{{ $t('invoice.supplier_fiche_no_preview') }}</p>
			</div>
		</div>

		<div class="col-12 col-xl-7">
			<div class="box invoice-supplier-details">
			<LoadingSpinner v-if="!loaded" />

			<template v-else>
				<LinesVersions
					v-if="versions.length > 1"
					class="mb-3"
					:versions="versions"
					:current="source"
					:ai="ai"
					@select="showVersion"
					@launch="launchAI"
					@retry="retryAI"
				/>

				<!-- Une analyse arrivée pendant que l'utilisateur retouchait la précédente ne la remplace
				     pas d'office : ses retouches restent à l'écran jusqu'à ce qu'il choisisse. -->
				<b-alert v-if="ai.pending" show variant="info">
					{{ $t('invoice.supplier_details_ai_ready') }}
					<button class="btn btn-primary btn-sm ml-2" @click="applyAITask(ai.pending, true)">{{ $t('invoice.supplier_details_ai_ready_show') }}</button>
				</b-alert>

				<!-- Des prix toutes taxes comprises ne se déduisent pas en silence : la conversion ferait
				     entrer en comptabilité un montant que personne n'a lu. -->
				<b-alert v-if="ttc_rate !== null" show variant="warning">
					{{ $t('invoice.supplier_details_ai_ttc_question', { rate: percentLabel(ttc_rate) }) }}
					<div class="mt-2">
						<button class="btn btn-primary btn-sm" @click="answerTTC(true)">{{ $t('invoice.supplier_details_ai_ttc_convert') }}</button>
						<button class="btn btn-secondary btn-sm ml-2" @click="answerTTC(false)">{{ $t('invoice.supplier_details_ai_ttc_keep') }}</button>
					</div>
				</b-alert>

				<!-- Le total peut tomber juste alors qu'une ligne a été rangée sous le cheval voisin : le
				     sous-total que la facture imprime pour chaque cheval le révèle. -->
				<b-alert v-if="horse_mismatches.length" show variant="warning">
					{{ $t('invoice.supplier_details_ai_horse_mismatch', { count: horse_mismatches.length }) }}
					<ul class="mb-0 mt-1">
						<li v-for="mismatch in horse_mismatches" :key="mismatch.horse">
							{{ $t('invoice.supplier_details_ai_horse_mismatch_line', { horse: mismatch.horse, printed: amountLabel(mismatch.printed), read: amountLabel(mismatch.read) }) }}
						</li>
					</ul>
					<button v-if="thorough_available" class="btn btn-primary btn-sm mt-2" :disabled="!!ai.running" @click="launchAI(ai.task.aitask_provider, true)">
						{{ $t('invoice.supplier_details_ai_relaunch_thorough') }}
					</button>
				</b-alert>

				<p v-if="read_only" class="invoice-supplier-details__hint">
					{{ $t('invoice.supplier_details_from_xml') }}
				</p>
				<!-- Une proposition de la lecture optique et un détail déjà recontrôlé se présentent de la
				     même façon : sans cette mention, rien ne dit s'il reste quelque chose à vérifier. Elle
				     ne vaut que pour les lignes en base. -->
				<b-alert v-else-if="saved_origin && source === 'saved'" show :variant="saved_origin === 'ocr' ? 'warning' : 'success'">
					{{ $t('invoice.supplier_details_status_' + saved_origin) }}
				</b-alert>

				<!-- Le cheval s'affecte à des lignes cochées plutôt que ligne par ligne : une facture de
				     pension répète les mêmes articles pour chaque cheval, et on les rattache par paquets.
				     Un seul sélecteur sert toute la facture, ouvert le temps de désigner le cheval. -->
				<div v-if="lines.length" class="invoice-supplier-details__horse-bar">
					<template v-if="horse_target">
						<span>{{ $t(horse_target === 'all' ? 'invoice.supplier_details_horse_pick_all' : 'invoice.supplier_details_horse_pick') }}</span>
						<SearchHorse class="invoice-supplier-details__horse-search" @update:horse_selected="onHorsePicked" />
						<button class="btn btn-secondary btn-sm" @click="horse_target = null">{{ $t('global.annuler') }}</button>
					</template>
					<template v-else-if="selected_lines.length">
						<span>{{ $t('invoice.supplier_details_horse_selection', { count: selected_lines.length }) }}</span>
						<button class="btn btn-primary btn-sm" @click="horse_target = 'selection'">{{ $t('invoice.supplier_details_horse_assign') }}</button>
						<button class="btn btn-secondary btn-sm" @click="openDateEditor(selected_lines, null)">{{ $t('invoice.supplier_details_date_bulk') }}</button>
						<button v-if="selection_has_horse" class="btn btn-secondary btn-sm" @click="unassignHorse(selected_lines)">
							{{ $t('invoice.supplier_details_horse_unassign') }}
						</button>
						<button class="btn btn-link btn-sm" @click="clearSelection">{{ $t('invoice.supplier_details_horse_unselect') }}</button>
					</template>
					<template v-else>
						<button class="btn btn-secondary btn-sm" @click="horse_target = 'all'">{{ $t('invoice.supplier_details_horse_assign_all') }}</button>
						<template v-if="has_horse_groups">
							<button class="btn btn-link btn-sm" @click="setGroupsCollapsed(true)">{{ $t('invoice.supplier_details_horse_collapse_all') }}</button>
							<button class="btn btn-link btn-sm" @click="setGroupsCollapsed(false)">{{ $t('invoice.supplier_details_horse_expand_all') }}</button>
						</template>
						<span class="invoice-supplier-details__hint">{{ $t('invoice.supplier_details_horse_coverage', { assigned: assigned_count, total: lines.length }) }}</span>
					</template>
				</div>

				<!-- Dater d'un coup les lignes cochées : une facture de pension porte la même période sur
				     toutes ses lignes. -->
				<LineDateEditor
					v-if="date_edit && !date_edit.line"
					:key="date_edit.opened_at"
					class="mb-3"
					:title="$t('invoice.supplier_details_date_bulk_title', { count: date_edit.lines.length })"
					:start-date="date_edit.lines[0].isdetails_start_date"
					:end-date="date_edit.lines[0].isdetails_end_date"
					@apply="applyDates(date_edit.lines, $event)"
					@cancel="date_edit = null"
				/>

				<!-- Pas d'enveloppe table-responsive : le thème y attache sa présentation des listes sur
				     téléphone (cellules en ligne, cases de sélection), et ce tableau a la sienne. Il tient
				     sur un écran large et s'empile sur un écran étroit, sans défilement horizontal. -->
				<table class="table mb-0 invoice-supplier-details__table invoice-supplier-details__lines">
					<!-- Une case modifiable porte son propre libellé : l'en-tête ne sert qu'aux lignes du
					     vendeur, qui s'affichent en texte. -->
					<thead v-if="read_only">
						<tr>
							<th class="text-right">{{ $t('invoice.supplier_details_qty') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_part') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_unit_price') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_discount') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_ht') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_vat') }}</th>
							<th></th>
						</tr>
					</thead>

					<tbody v-if="!lines.length">
						<tr>
							<td :colspan="columns_count" class="text-center">{{ $t('invoice.supplier_details_empty') }}</td>
						</tr>
					</tbody>

					<!-- Les lignes se rangent sous un intercalaire par cheval, dans l'ordre où chacun apparaît
					     sur la facture, et celles qui n'en ont pas sous un dernier bloc. Tant qu'aucun cheval
					     n'est affecté, les intercalaires n'apportent rien et le tableau reste tel quel. -->
					<template v-for="(group, group_index) in horse_groups">
						<!-- Un filet entoure l'intercalaire et ses lignes, et un espace sépare deux chevaux :
						     ce qui est rattaché à chacun se voit d'un coup d'œil. Le tableau ne permet pas
						     d'envelopper plusieurs tbody : le cadre est tracé sur leurs cellules. -->
						<tbody v-if="has_horse_groups && group_index > 0" :key="'gap-' + group.key" class="invoice-supplier-details__horse-gap" aria-hidden="true">
							<tr><td :colspan="columns_count"></td></tr>
						</tbody>
						<tbody
							v-if="has_horse_groups"
							:key="'horse-' + group.key"
							class="invoice-supplier-details__horse"
							:class="{ 'invoice-supplier-details__horse--unresolved': group.read, 'invoice-supplier-details__horse--collapsed': group.collapsed }"
						>
							<tr>
								<td :colspan="columns_count">
									<div class="invoice-supplier-details__horse-header">
										<!-- Les lignes d'un cheval se replient, pour relire la facture cheval par
										     cheval. -->
										<button
											type="button"
											class="btn btn-link btn-sm p-0"
											:aria-expanded="group.collapsed ? 'false' : 'true'"
											:title="$t(group.collapsed ? 'invoice.supplier_details_horse_expand' : 'invoice.supplier_details_horse_collapse')"
											@click="toggleGroup(group)"
										>
											<font-awesome-icon :icon="['fal', 'chevron-right']" :rotation="group.collapsed ? null : 90" fixed-width />
										</button>
										<b-form-checkbox :checked="group.all_selected" @change="selectGroup(group, $event)">
											<strong>{{ group.name }}</strong>
											<span v-if="group.collapsed" class="invoice-supplier-details__hint">{{ $t('invoice.supplier_details_horse_lines_count', { count: group.lines.length }) }}</span>
											<span v-if="group.sire" class="invoice-supplier-details__hint">{{ $t('invoice.supplier_details_horse_sire', { sire: group.sire }) }}</span>
										</b-form-checkbox>
										<!-- Ce que l'analyse a fait du cheval lu se voit d'emblée : reconnu dans la
										     licence, et par quelle clé, ou inconnu et à rattacher. -->
										<b-badge v-if="group.match" variant="success">
											<font-awesome-icon :icon="['far', 'check']" /> {{ $t('invoice.supplier_details_horse_match_' + group.match) }}
										</b-badge>
										<template v-if="group.read">
											<b-badge variant="warning">{{ $t('invoice.supplier_details_horse_read_unresolved') }}</b-badge>
											<span v-if="group.read_ids" class="invoice-supplier-details__hint">{{ group.read_ids }}</span>
										</template>
										<span class="invoice-supplier-details__horse-total">{{ group.total }}</span>
										<!-- Un cheval que le modèle a lu sans le retrouver chez vous : l'utilisateur le
										     crée — dans une modale, recherche prête —, le désigne parmi les siens, ou
										     range ses lignes parmi celles sans cheval. -->
										<template v-if="group.read">
											<a href="#" @click.prevent="openHorseCreation(group)">
												<font-awesome-icon :icon="['fal', 'plus-circle']" /> {{ $t('invoice.supplier_details_horse_create') }}
											</a>
											<a href="#" @click.prevent="attachGroup(group)">{{ $t('invoice.supplier_details_horse_attach') }}</a>
											<a href="#" @click.prevent="leaveGroup(group)">{{ $t('invoice.supplier_details_horse_leave') }}</a>
										</template>
										<template v-if="group.horse">
											<b-form-checkbox :checked="group.all_rebillable" :indeterminate="group.some_rebillable && !group.all_rebillable" @change="setGroupRebillable(group, $event)">
												{{ $t('invoice.supplier_details_rebillable') }}
											</b-form-checkbox>
											<a v-if="!read_only" href="#" @click.prevent="addLine(group.horse)">
												<font-awesome-icon :icon="['fal', 'plus']" /> {{ $t('invoice.supplier_details_add') }}
											</a>
											<a href="#" @click.prevent="unassignHorse(group.lines)">{{ $t('invoice.supplier_details_horse_unassign') }}</a>
										</template>
									</div>
								</td>
							</tr>
						</tbody>

						<!-- Une ligne de facture occupe trois rangées : son libellé sur la largeur des chiffres,
						     parce qu'il est long et que c'est lui qu'on relit sur le document, puis ses
						     chiffres, et enfin le calcul qui les relie. Un tbody par ligne les garde
						     solidaires. -->
						<tbody
							v-for="({ line, index }, position) in group.shown_entries"
							:key="line.isdetails_id || 'new-' + index"
							class="invoice-supplier-details__line"
							:class="{
								'invoice-supplier-details__line--mismatch': hasAmountMismatch(line),
								'invoice-supplier-details__line--grouped': has_horse_groups,
								'invoice-supplier-details__line--group-end': has_horse_groups && position === group.shown_entries.length - 1
							}"
						>
							<tr>
								<!-- Le libellé et l'article sous lequel la ligne se range tiennent dans une seule
								     case. Par défaut l'article porte le libellé exact de la ligne ; le chevron
								     accolé ouvre ceux que ce fournisseur a déjà facturés — reprendre l'un d'eux
								     suffit à rapprocher la ligne — ou en nomme un nouveau. Deux factures
								     mensuelles dont le vendeur date le libellé se rangent ainsi sous un même
								     article. Le libellé appartient au vendeur sur une facture électronique : seul
								     l'article s'y choisit alors. -->
								<td :colspan="columns_count - 1" class="invoice-supplier-details__label">
									<div class="invoice-supplier-details__selectable">
										<b-form-checkbox v-model="line.selected" :aria-label="$t('invoice.supplier_details_horse_select_line')" />
										<b-input-group class="invoice-supplier-details__designation">
											<div class="invoice-supplier-details__field invoice-supplier-details__field--text">
												<span class="invoice-supplier-details__field-label">
													{{ line.article_renamed ? $t('invoice.supplier_details_article_named', { article: line.article_label }) : $t('invoice.supplier_details_label') }}
												</span>
												<b-input
													v-model="line.isdetails_label"
													:readonly="line.locked"
													:state="line.isdetails_label ? null : false"
													@change="onLabelChanged(line)"
												/>
											</div>
											<b-input-group-append>
												<b-dropdown
													right
													variant="outline-secondary"
													:title="$t('invoice.supplier_details_article_choose')"
													:popper-opts="{ positionFixed: true }"
													menu-class="invoice-supplier-details__article-menu"
													@show="article_search = ''"
												>
													<!-- Entrée ne soumet rien : le formulaire n'est là que pour la recherche. -->
													<b-dropdown-form @submit.prevent>
														<b-input v-model="article_search" size="sm" :placeholder="$t('invoice.supplier_details_article_search')" />
													</b-dropdown-form>
													<b-dropdown-item v-if="line.article_renamed" @click="onArticleChosen(line, line.isdetails_label)">
														{{ $t('invoice.supplier_details_article_same') }}
													</b-dropdown-item>
													<b-dropdown-item
														v-for="label in filtered_article_labels"
														:key="label"
														:active="line.article_label === label"
														@click="onArticleChosen(line, label)"
													>
														{{ label }}
														<span v-if="article_hints[label]" class="invoice-supplier-details__option-hint">({{ article_hints[label] }})</span>
													</b-dropdown-item>
													<b-dropdown-item v-if="article_search_label && !article_labels.includes(article_search_label)" @click="onArticleChosen(line, article_search_label)">
														{{ $t('invoice.supplier_details_article_create', { article: article_search_label }) }}
													</b-dropdown-item>
												</b-dropdown>
											</b-input-group-append>
										</b-input-group>
									</div>
								</td>
								<td class="invoice-supplier-details__label invoice-supplier-details__actions">
									<!-- L'historique suit l'article enregistré : un article renommé à l'écran n'en
									     a pas encore, ses lignes ne s'y rangeront qu'à l'enregistrement. -->
									<a v-if="line.isdetails_article" href="#" class="mr-2" :title="$t('invoice.supplier_article_prices_open')" @click.prevent="$refs.prices.open(line.isdetails_article.isarticles_label, invoice.suppliersocr_tiers)">
										<font-awesome-icon :icon="['far', 'chart-area']" />
									</a>
									<a v-if="!line.locked" href="#" @click.prevent="removeLine(line)">
										<font-awesome-icon :icon="['fal', 'trash-alt']" />
									</a>
								</td>
							</tr>
							<!-- Chaque case porte son libellé en petit au-dessus de sa valeur : le tableau
							     s'étend sur plusieurs rangées par ligne, et un en-tête unique ne dit plus à
							     quelle case il se rapporte dès qu'on a fait défiler quelques lignes.

							     Les cases suivent l'ordre du calcul qui mène au montant : quantité, quote-part,
							     prix unitaire, remise, puis le montant lui-même et son taux. Toutes restent
							     visibles, la remise et la quote-part comprises : les ouvrir à la demande
							     cachait justement le facteur qui faisait diverger une ligne. -->
							<tr class="invoice-supplier-details__figures">
								<!-- La quantité accepte un prorata, 17/30 : le prix unitaire est alors celui de
								     trente unités, et la quantité en facture dix-sept. Son unité lui est accolée,
								     comme sur le document où elle se lit avec elle. Les listes s'ouvrent en
								     position fixe pour que le défilement du tableau ne les coupe pas. -->
								<td class="text-right">
									<span v-if="line.locked">{{ line.qty_input }} {{ unitLabel(line.isdetails_unit) }}</span>
									<b-input-group v-else class="invoice-supplier-details__group">
										<div class="invoice-supplier-details__field">
											<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_qty') }}</span>
											<b-input v-select-on-focus :tabindex="index * 4 + 1" v-model="line.qty_input" :state="factorState(line, 'qty')" @blur.native="onFactorLeft(line)" />
										</div>
										<b-input-group-append>
											<b-dropdown size="sm" variant="outline-secondary" right :text="unitLabel(line.isdetails_unit)" :popper-opts="{ positionFixed: true }" menu-class="invoice-supplier-details__unit-menu">
												<b-dropdown-item
													v-for="option in unit_options"
													:key="option.value"
													:active="line.isdetails_unit === option.value"
													@click="setUnit(line, option.value)"
												>
													{{ option.text }}
												</b-dropdown-item>
											</b-dropdown>
										</b-input-group-append>
									</b-input-group>
								</td>
								<td class="text-right">
									<span v-if="line.locked">{{ percentLabel(line.isdetails_part) }}</span>
									<b-input-group v-else size="sm" append="%" class="invoice-supplier-details__group">
										<div class="invoice-supplier-details__field">
											<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_part') }}</span>
											<b-input v-select-on-focus :tabindex="index * 4 + 2" v-model="line.isdetails_part" :state="factorState(line, 'part')" @blur.native="onPartLeft(line)" />
										</div>
									</b-input-group>
								</td>
								<td class="text-right">
									<span v-if="line.locked">{{ line.isdetails_htunit }}</span>
									<div v-else class="invoice-supplier-details__field">
										<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_unit_price') }}</span>
										<PriceInput :tabindex="index * 4 + 3" v-model="line.isdetails_htunit" @blur.native="onFactorLeft(line)" />
									</div>
								</td>
								<!-- Une seule case pour les trois formes de remise : la liste accolée dit
								     laquelle s'applique et permet d'en changer, et taper « % », « €/u » ou « € »
								     derrière le nombre fait de même sans quitter le clavier. -->
								<td class="text-right">
									<span v-if="line.locked">{{ discountLabel(line) }}</span>
									<b-input-group v-else class="invoice-supplier-details__group">
										<div class="invoice-supplier-details__field">
											<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_discount') }}</span>
											<b-input v-select-on-focus :tabindex="index * 4 + 4" v-model="line.isdetails_discount" :state="factorState(line, 'discount')" @blur.native="onDiscountLeft(line)" />
										</div>
										<b-input-group-append>
											<b-dropdown size="sm" variant="outline-secondary" right :text="discountUnit(line.isdetails_discount_type)" :popper-opts="{ positionFixed: true }">
												<b-dropdown-item
													v-for="type in discount_types"
													:key="type"
													:active="line.isdetails_discount_type === type"
													@click="setDiscountType(line, type)"
												>
													{{ discountUnit(type) }} — {{ $t('invoice.supplier_details_discount_types.' + type) }}
												</b-dropdown-item>
											</b-dropdown>
										</b-input-group-append>
									</b-input-group>
								</td>
								<td class="text-right" :title="hasAmountMismatch(line) ? $t('invoice.supplier_details_mismatch') : ''">
									<span v-if="line.locked">{{ line.isdetails_ht }}</span>
									<div v-else class="invoice-supplier-details__field">
										<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_ht') }}</span>
										<PriceInput v-model="line.isdetails_ht" @change="refreshControl" />
									</div>
								</td>
								<td class="text-right">
									<span v-if="line.locked">{{ percentLabel(line.isdetails_vat_percent) }}</span>
									<b-input-group v-else size="sm" append="%" class="invoice-supplier-details__group">
										<div class="invoice-supplier-details__field">
											<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_vat') }}</span>
											<b-input v-select-on-focus v-model="line.isdetails_vat_percent" @change="onRateChanged(line)" />
										</div>
									</b-input-group>
								</td>
								<!-- Seule une ligne rattachée à un cheval se refacture : c'est lui qui désigne le
								     client à qui la refacturer. -->
								<td class="invoice-supplier-details__actions">
									<b-form-checkbox v-if="line.isdetails_horse" v-model="line.isdetails_rebillable" class="invoice-supplier-details__rebillable">
										{{ $t('invoice.supplier_details_rebillable') }}
									</b-form-checkbox>
								</td>
							</tr>
							<!-- Le calcul qui mène au montant, écrit en clair dans l'ordre des cases : on y lit
							     d'un coup d'œil lequel des facteurs fait diverger une ligne. -->
							<!-- La date d'acte ou la période se lit dans la rangée du calcul, sans colonne de
							     plus : une pastille qui s'ouvre sur sa saisie. Elle reste visible vide, pour
							     qu'on sache où dater une ligne. -->
							<tr class="invoice-supplier-details__formula">
								<td :colspan="columns_count - 1">
									<div class="invoice-supplier-details__formula-row">
										<button
											type="button"
											class="invoice-supplier-details__date-chip"
											:class="{ 'invoice-supplier-details__date-chip--empty': !line.isdetails_start_date, 'invoice-supplier-details__date-chip--open': date_edit && date_edit.line === line }"
											@click="openDateEditor([line], line)"
										>
											<font-awesome-icon :icon="['fal', 'calendar-alt']" /> {{ dateLabel(line) }}
										</button>
										<span v-if="prorataMismatch(line)" class="text-danger invoice-supplier-details__hint">{{ prorataMismatch(line) }}</span>
										<span class="invoice-supplier-details__hint invoice-supplier-details__formula-text">{{ formulaLabel(line) }}</span>
									</div>
								</td>
								<td></td>
							</tr>
							<tr v-if="date_edit && date_edit.line === line">
								<td :colspan="columns_count - 1">
									<LineDateEditor
										:start-date="line.isdetails_start_date"
										:end-date="line.isdetails_end_date"
										:group-size="has_horse_groups ? group.lines.length : 0"
										@apply="applyDates([line], $event)"
										@apply-group="applyDates(group.lines, $event)"
										@cancel="date_edit = null"
									/>
								</td>
								<td></td>
							</tr>
							<tr v-if="priceDrift(line)">
								<td :colspan="columns_count - 1">
									<div class="alert alert-warning py-1 mb-0">
										{{ $t(priceDrift(line).percent > 0 ? 'invoice.supplier_details_price_drift_up' : 'invoice.supplier_details_price_drift_down', priceDrift(line)) }}
									</div>
								</td>
								<td></td>
							</tr>
						</tbody>
					</template>
				</table>

				<button v-if="!read_only" class="btn btn-secondary mt-3" @click="addLine(null)">
					<font-awesome-icon :icon="['fal', 'plus']" /> {{ $t('invoice.supplier_details_add') }}
				</button>

				<div v-if="control" class="invoice-supplier-details__control mt-4">
					<h4>{{ $t('invoice.supplier_details_control') }}</h4>

					<table class="table table-sm mb-0 invoice-supplier-details__totals">
						<thead>
							<tr>
								<th></th>
								<th class="text-right">{{ $t('invoice.supplier_details_control_invoice') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_details_control_lines') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr :class="{ 'text-danger': !control.ht }">
								<th>{{ $t('invoice.suppliersocr_ht') }}</th>
								<td class="text-right">{{ amounts.invoice_ht }}</td>
								<td class="text-right">{{ amounts.lines_ht }}</td>
							</tr>
							<tr :class="{ 'text-danger': !control.vat }">
								<th>{{ $t('invoice.suppliersocr_ttc') }}</th>
								<td class="text-right">{{ amounts.invoice_ttc }}</td>
								<td class="text-right">{{ amounts.lines_ttc }}</td>
							</tr>
						</tbody>
					</table>

					<div class="mt-3">
						<ErrorAlert v-if="missing_label" messageI18n="invoice.supplier_details_missing_label" />
						<ErrorAlert v-else-if="invalid_factors" messageI18n="invoice.supplier_details_invalid_factors" />
						<ErrorAlert v-else-if="!balanced" messageI18n="invoice.supplier_details_unbalanced" />

						<button class="btn btn-primary" :disabled="!balanced || missing_label || invalid_factors || saving" @click="saveLines">
							{{ $t('invoice.supplier_details_save') }}
							<font-awesome-icon v-if="saving" :icon="['fal', 'spinner']" spin />
						</button>
					</div>

					<template v-if="rates.length">
						<!-- La référence par taux ne vient que de la lecture optique. Une facture reçue
						     par voie électronique n'en a aucune — sa prédiction se réduit à l'identité du
						     vendeur — et une facture lue peut n'avoir livré aucun taux. Le tableau reste
						     alors utile comme ventilation des lignes, mais il n'y a rien à confronter, et
						     rien à mettre en garde. -->
						<p v-if="has_reference" class="invoice-supplier-details__hint mt-3 mb-1">
							{{ $t('invoice.supplier_details_control_reference_origin') }}
						</p>

						<table class="table table-sm mb-0 mt-3">
							<thead>
								<tr>
									<th>{{ $t('invoice.supplier_details_vat') }}</th>
									<th class="text-right">{{ $t('invoice.supplier_details_control_lines') }}</th>
									<th v-if="has_reference" class="text-right">{{ $t('invoice.supplier_details_control_reference') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="rate in rates" :key="rate.key" :class="{ 'text-danger': rate.match === false }">
									<td>{{ rate.label }}</td>
									<td class="text-right">{{ rate.lines }}</td>
									<td v-if="has_reference" class="text-right">{{ rate.reference }}</td>
								</tr>
							</tbody>
						</table>
					</template>
				</div>
			</template>
		</div>
		</div>

		<!-- Mêmes formulaires que l'ajout d'un cheval depuis un contrat : recherche dans les référentiels,
		     ou saisie manuelle pour un cheval qu'ils ignorent. Le contenu d'un slot se rend avec cet écran,
		     modale fermée : il attend qu'un cheval lu soit choisi, et se recrée pour chacun — les formulaires
		     ne lisent leur recherche qu'à l'initialisation. -->
		<b-modal ref="horseCreationModal" size="xl" hide-footer :title="$t('invoice.supplier_details_horse_create')">
			<b-tabs v-if="horse_creation_read" :key="JSON.stringify(horse_creation_read)" content-class="mt-3" class="add-horse-tabs">
				<b-tab :title="$t('horse.ajout_unitaire')">
					<AddHorse
						from="tiers"
						:auto-search="true"
						:horseToAddName="horse_creation_read.transponder || horse_creation_read.sire || horse_creation_read.name"
						@update:horse_id="onHorseCreated"
					/>
				</b-tab>
				<b-tab :title="$t('horse.manual_add')" lazy>
					<HorseCreation from="tiers" :horseToAddName="horse_creation_read.name" @update:horse_id="onHorseCreated" />
				</b-tab>
			</b-tabs>
		</b-modal>

		<ArticlePriceModal ref="prices" />
	</div>
</template>


<script type="text/javascript">
	/* global BigInt */
	// Déclaré ici parce que la configuration ESLint du dépôt ne connaît que l'environnement node, sans
	// les globales d'ES2020. Le navigateur, lui, l'a : la cible de compilation ne descend pas plus bas.

	import AITask from "@/mixins/AITask.js"
	import Horse from "@/mixins/Horse.js"
	import Invoice from "@/mixins/Invoice.js"
	import Payment from "@/mixins/Payment.js"

	// Type de traitement déclaré par le serveur pour la lecture des lignes d'une facture fournisseur.
	const AI_TYPE = 'supplier_invoice_lines'
	// Intervalle de relève d'un traitement en cours : un appel au modèle prend de quelques secondes à
	// deux minutes.
	const AI_POLL_MS = 3000

	export default {
		name: "InvoiceSupplierFicheDetails",

		components: {
			AddHorse: () => import('@/components/Horse/Ajout/AddHorse'),
			ArticlePriceModal: () => import('@/components/InvoiceSupplier/ArticlePriceModal'),
			ErrorAlert: () => import('GroomyRoot/components/Alert/ErrorAlert'),
			HorseCreation: () => import('@/components/Horse/Ajout/AjoutManuel'),
			LineDateEditor: () => import('@/components/InvoiceSupplier/Fiche/LineDateEditor'),
			LinesVersions: () => import('@/components/InvoiceSupplier/Fiche/LinesVersions'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			PriceInput: () => import('GroomyRoot/components/Inputs/PriceInput'),
			SearchHorse: () => import('@/components/Contract/SearchHorse')
		},

		mixins: [AITask, Horse, Invoice, Payment],

		props: {
			invoice: { type: Object, default: () => ({}) },
			invoicesupplier_id: { type: [String, Number], default: null }
		},

		data() {
			return {
				loaded: false,
				saving: false,
				lines: [],
				// Origine des lignes telles qu'elles sont en base : 'ocr' tant que le détail n'est qu'une
				// proposition de la lecture optique, 'manual' une fois enregistré, null sans ligne. Elle se
				// relève au chargement et à l'enregistrement, et non sur l'écran : une ligne ajoutée mais
				// pas encore déposée ne fait pas d'un détail un détail vérifié.
				saved_origin: null,
				// Version du détail affichée : 'saved' pour les lignes en base — qui sont la lecture du
				// document tant qu'elles n'ont pas été enregistrées —, 'ocr' pour la lecture optique relue
				// à côté de lignes déjà enregistrées, 'ai' pour l'analyse par modèle de langage.
				source: 'saved',
				// Lignes de chaque version, retouches comprises : passer d'une version à l'autre ne perd
				// rien de ce qui a été fait sur la précédente. lines est celle de la version affichée.
				drafts: {},
				// Empreinte de chaque version telle qu'elle a été chargée, pour dire si elle a été retouchée.
				signatures: {},
				// Équilibre des totaux de chaque version, tel que le dernier contrôle l'a jugé.
				balances: {},
				// Lignes en base et lecture optique, telles que le serveur les a rendues.
				saved_lines: [],
				ocr_lines: [],
				ai: {
					// Fournisseurs que la licence peut interroger, et celui choisi la dernière fois.
					providers: [],
					last: null,
					// Dernière analyse aboutie, en cours, en échec, et celle arrivée pendant des retouches.
					task: null,
					running: null,
					failed: null,
					pending: null
				},
				ai_poll: null,
				// Saisie de date ouverte : les lignes qu'elle datera, et la ligne sous laquelle elle s'ouvre
				// — null pour les lignes cochées, datées depuis la barre de sélection.
				date_edit: null,
				// Intercalaires repliés, par clé d'intercalaire. Ils le restent d'une version à l'autre : la
				// relecture se fait cheval par cheval, quelle que soit la version affichée.
				collapsed_groups: {},
				// Cheval lu sur la facture que l'utilisateur est en train de créer.
				horse_creation_read: null,
				// Réponse à la question des prix toutes taxes comprises, par analyse.
				ttc_answers: {},
				// Articles déjà facturés par ce fournisseur, proposés sur le libellé des lignes.
				articles: [],
				// Articles choisis à l'écran pour un libellé, par forme comparable : ils priment sur les
				// libellés rangés que le serveur connaît, jusqu'à l'enregistrement. null rend le libellé à
				// son propre article.
				chosen_articles: {},
				// Recherche dans la liste des articles. Une seule liste s'ouvre à la fois : elle est
				// partagée, et remise à blanc à chaque ouverture.
				article_search: '',
				// Lignes auxquelles le cheval en cours de recherche sera affecté : 'selection' pour les
				// lignes cochées, 'all' pour toute la facture, null tant que le sélecteur est fermé.
				horse_target: null,
				control: null,
				// Montants du contrôle, formatés dans une méthode : priceFormat attend la devise, et un
				// computed rendrait des promesses.
				amounts: {
					lines_ht: '',
					invoice_ht: '',
					lines_ttc: '',
					invoice_ttc: ''
				},
				rates: []
			}
		},

		computed: {
			/**
			 * Langue dans laquelle s'écrivent les montants. Le mode « code », qui affiche les clés de
			 * traduction, n'est pas une langue : les montants s'y écrivent en français.
			 */
			number_locale() {
				return this.$i18n.locale() === 'code' ? 'fr' : this.$i18n.locale()
			},

			/**
			 * Dit si la ventilation par taux a quelque chose à quoi se confronter.
			 *
			 * La référence ne vient que de la lecture optique : une facture reçue par voie électronique
			 * n'en a pas, et une facture lue n'en a pas toujours. Sans elle, le tableau des taux n'est
			 * qu'une ventilation des lignes — l'annoncer comme une comparaison serait mentir.
			 */
			has_reference() {
				return this.rates.some(rate => rate.reference !== '')
			},

			/**
			 * Dit si une ligne attend encore son libellé.
			 *
			 * Le serveur l'exige, et il refuse l'envoi entier : sans ce contrôle, ajouter une ligne puis
			 * oublier de la nommer ferait échouer le dépôt de toutes les autres, avec un message qui ne
			 * dirait pas laquelle est en cause.
			 */
			missing_label() {
				return this.lines.some(line => !line.isdetails_label)
			},

			/**
			 * Dit si le détail peut être déposé.
			 *
			 * Les deux totaux doivent retomber sur ceux de la facture : le hors taxes, et la taxe — donc
			 * le toutes taxes, qui en est la somme. Tant que l'un des deux diverge, les lignes racontent
			 * autre chose que la facture, et les enregistrer ferait porter cette contradiction à la
			 * ventilation comptable puis à l'export.
			 */
			balanced() {
				// Sur une facture dont le vendeur a déclaré toutes les lignes, l'enregistrement ne touche
				// ni aux montants ni aux quantités : il ne porte que le nom de l'article, qui est à nous.
				// L'équilibre des totaux n'a alors rien à autoriser.
				if(this.read_only) return true

				return !!this.control && this.control.ht && this.control.vat
			},

			/**
			 * Lignes cochées, sur lesquelles portent l'affectation et le retrait du cheval.
			 */
			selected_lines() {
				return this.lines.filter(line => line.selected)
			},

			/**
			 * Dit si l'une des lignes cochées porte un cheval : le retrait n'est proposé qu'alors.
			 */
			selection_has_horse() {
				return this.selected_lines.some(line => line.isdetails_horse)
			},

			/**
			 * Nombre de lignes déjà rattachées à un cheval.
			 */
			assigned_count() {
				return this.lines.filter(line => line.isdetails_horse).length
			},

			/**
			 * Dit si au moins une ligne porte un cheval, affecté ou seulement lu sur la facture, et donc si
			 * le tableau se range en intercalaires.
			 */
			has_horse_groups() {
				return this.lines.some(line => line.isdetails_horse || this.readHorseName(line))
			},

			/**
			 * Lignes rangées par cheval, dans l'ordre où chacun apparaît sur la facture, suivies de celles
			 * qui n'en portent aucun.
			 *
			 * Chaque ligne garde son rang dans la facture : c'est lui qui fixe l'ordre de tabulation et
			 * l'ordre enregistré, que le regroupement ne fait qu'afficher autrement.
			 */
			horse_groups() {
				const groups = []
				const by_horse = {}
				const without = { key: 'none', horse: null, entries: [] }

				this.lines.forEach((line, index) => {
					const horse = line.isdetails_horse
					let group = without

					if(horse) {
						if(!by_horse[horse.horse_id]) {
							by_horse[horse.horse_id] = { key: horse.horse_id, horse, entries: [] }
							groups.push(by_horse[horse.horse_id])
						}

						group = by_horse[horse.horse_id]
					}
					// Un cheval lu sur la facture mais introuvable chez vous garde son propre intercalaire :
					// ses lignes se rattachent ensemble, comme sur le document.
					else if(this.readHorseName(line)) {
						const key = 'read-' + this.normalizeLabel(this.readHorseName(line))

						if(!by_horse[key]) {
							by_horse[key] = { key, horse: null, read: this.readHorseName(line), entries: [] }
							groups.push(by_horse[key])
						}

						group = by_horse[key]
					}

					group.entries.push({ line, index })
				})

				if(without.entries.length) groups.push(without)

				return groups.map(group => {
					const lines = group.entries.map(entry => entry.line)

					return {
						...group,
						lines,
						name: group.horse ? group.horse.horse_nom : (group.read ? this.$t('invoice.supplier_details_horse_read', { name: group.read }) : this.$t('invoice.supplier_details_horse_none')),
						collapsed: !!this.collapsed_groups[group.key],
						// Un intercalaire replié garde son en-tête, total compris ; seules ses lignes se masquent.
						shown_entries: this.has_horse_groups && this.collapsed_groups[group.key] ? [] : group.entries,
						read_ids: group.read ? this.readHorseIds(lines[0].horse_read) : null,
						sire: group.horse ? group.horse.horse_sire : null,
						// Clé par laquelle l'analyse a retrouvé le cheval, tant que personne ne l'a changé.
						match: group.horse ? (lines.find(line => line.horse_match) || {}).horse_match : null,
						total: this.totalLabel(lines),
						all_selected: lines.every(line => line.selected),
						all_rebillable: lines.every(line => line.isdetails_rebillable),
						some_rebillable: lines.some(line => line.isdetails_rebillable)
					}
				})
			},

			/**
			 * Dit si le vendeur a déclaré lui-même les lignes de la facture, sur une facture électronique.
			 */
			vendor_lines() {
				return this.saved_lines.some(line => line.isdetails_origin === 'xml')
			},

			/**
			 * Versions du détail proposées, dans l'ordre de la carte : la lecture du document, l'analyse,
			 * puis les lignes enregistrées.
			 *
			 * Tant que rien n'a été enregistré, les lignes en base sont la lecture du document elle-même ;
			 * une fois enregistrées, la lecture optique se relit à part, à côté d'elles.
			 */
			versions() {
				const stored = !this.vendor_lines && this.saved_origin === 'manual'
				const versions = []

				if(stored && this.ocr_lines.length) versions.push(this.version('ocr', 'ocr'))
				if(!stored) versions.push(this.version('saved', this.vendor_lines ? 'xml' : 'ocr'))
				if(this.ai.providers.length || this.ai.task) versions.push(this.version('ai', 'ai'))
				if(stored) versions.push(this.version('saved', 'saved'))

				return versions
			},

			/**
			 * Taux de TVA des prix peut-être relevés toutes taxes comprises par l'analyse affichée, tant que
			 * l'utilisateur n'a pas répondu ; null sinon.
			 */
			ttc_rate() {
				if(this.source !== 'ai' || !this.ai.task || this.ttc_answers[this.ai.task.aitask_id] !== undefined) return null

				const ambiguous = this.ai.task.aitask_result.ttc_ambiguous

				return ambiguous ? ambiguous.rate : null
			},

			/**
			 * Chevaux dont les lignes de l'analyse affichée ne retombent pas sur le sous-total imprimé. Une
			 * analyse faite avant ce contrôle n'en porte pas.
			 */
			horse_mismatches() {
				if(this.source !== 'ai' || !this.ai.task) return []

				return this.ai.task.aitask_result.horse_mismatches || []
			},

			/**
			 * Une relecture par le modèle plus attentif du fournisseur peut être proposée : il en déclare un,
			 * et l'analyse affichée n'en vient pas déjà.
			 */
			thorough_available() {
				const provider = this.ai.providers.find(provider => provider.key === this.ai.task.aitask_provider)

				return !!provider && provider.thorough && !this.ai.task.aitask_thorough
			},

			/**
			 * Nombre de colonnes du tableau — quantité et son unité, quote-part, prix unitaire, remise,
			 * hors taxes, taux et la colonne de droite, qui porte la suppression à côté du libellé et la
			 * refacturation à côté des chiffres — dont dépendent les rangées pleine largeur et le message
			 * d'absence de ligne.
			 */
			columns_count() {
				return 7
			},

			/**
			 * Formes de remise proposées : pourcentage du prix unitaire, montant retranché de chaque unité,
			 * montant retranché une seule fois de la ligne.
			 */
			discount_types() {
				return ['percent', 'amount', 'line']
			},

			/**
			 * Devise du document. La fiche la donne toujours ; la valeur par défaut de la prop, elle, ne la
			 * porte pas.
			 */
			currency() {
				return this.invoice.suppliersocr_currency || 'EUR'
			},

			/**
			 * Symbole de la devise du document, qui désigne une remise en montant.
			 */
			currency_symbol() {
				return this.currencySymbol(this.currency)
			},

			/**
			 * Recherche saisie dans la liste des articles, débarrassée de ses espaces.
			 */
			article_search_label() {
				return this.article_search.trim()
			},

			/**
			 * Articles du fournisseur dont le libellé contient la recherche, sans tenir compte de la casse
			 * ni des accents.
			 *
			 * Calculés une fois pour toutes les lignes : chaque ligne a sa liste, mais une seule s'ouvre à
			 * la fois et toutes partagent la recherche.
			 */
			filtered_article_labels() {
				const needle = this.accentsTidy(this.article_search_label)

				if(!needle) return this.article_labels

				return this.article_labels.filter(label => this.accentsTidy(label).includes(needle))
			},

			/**
			 * Dit si une case de facteur est illisible ou hors de ses bornes.
			 *
			 * Le serveur refuse alors l'envoi entier, avec un message qui ne dirait pas quelle ligne est en
			 * cause : comme pour le libellé, l'enregistrement reste fermé tant qu'une case est en rouge.
			 */
			invalid_factors() {
				return this.lines.some(line => !line.locked && ['qty', 'part', 'discount'].some(factor => this.factorState(line, factor) === false))
			},

			/**
			 * Le document reçu est tantôt un PDF, tantôt une image selon la source : le premier
			 * s'affiche dans un cadre, la seconde directement.
			 */
			is_pdf() {
				return !!this.invoice.suppliersocr_pdf_url && this.invoice.suppliersocr_pdf_url.includes('.pdf')
			},

			/**
			 * Le détail ne se corrige pas quand le vendeur l'a déclaré lui-même.
			 *
			 * C'est l'origine des lignes qui le dit, et non la provenance de la facture : un dépôt
			 * manuel rattaché après coup à son document électronique en porte les lignes, et elles font
			 * foi tout autant.
			 */
			read_only() {
				return this.lines.some(line => line.locked)
			},

			/**
			 * Articles déjà facturés par ce fournisseur, tels que la liste les propose.
			 *
			 * Ce sont leurs libellés seuls, et non les articles eux-mêmes : c'est le libellé que la ligne
			 * porte et sur lequel le serveur retrouve l'article, et le champ reste ouvert à un nom
			 * qu'aucun d'eux ne porte encore. Le serveur les rend déjà rangés.
			 */
			article_labels() {
				return this.articles.map(article => article.isarticles_label)
			},

			/**
			 * Article sous lequel chaque libellé rangé doit tomber, par forme comparable : ceux que le
			 * serveur a retenus des corrections précédentes, puis ceux choisis depuis l'ouverture de
			 * l'écran, qui les remplacent.
			 */
			article_by_label() {
				const aliases = {}

				this.articles.forEach(article => {
					article.aliases.forEach(alias => { aliases[alias] = article.isarticles_label })
				})

				return { ...aliases, ...this.chosen_articles }
			},

			/**
			 * Précision affichée à côté de chaque suggestion : le code de l'article chez le vendeur, son
			 * unité, ou rien quand il n'en porte pas.
			 *
			 * Rangée par libellé une fois pour toutes plutôt que retrouvée à chaque suggestion affichée :
			 * la liste se rouvre à chaque ligne du tableau, et elle ne change qu'avec les articles.
			 */
			article_hints() {
				return Object.fromEntries(this.articles.map(article => [
					article.isarticles_label,
					[article.isarticles_code, this.unitLabel(article.isarticles_unit)].filter(part => part).join(' — ')
				]))
			},

			/**
			 * Unités proposées à la saisie, triées sur leur libellé.
			 *
			 * Les codes sont ceux d'UN/ECE Rec 20, le vocabulaire qu'une facture électronique déclare et
			 * que le serveur attend. La liste reprend celle de sa table de correspondance : un code
			 * qu'elle ne connaîtrait pas serait écarté à l'enregistrement, et l'unité disparaîtrait sans
			 * rien dire. Elle est énumérée ici plutôt que lue dans les traductions, dont le $t ne rend
			 * que des chaînes.
			 *
			 * Aucune entrée vide : une ligne modifiable porte toujours une unité, l'unité neutre à
			 * défaut d'une autre. Se remettre sur du vide ne mènerait qu'à la retrouver au rechargement.
			 */
			unit_options() {
				const codes = ['C62', 'KGM', 'GRM', 'TNE', 'LTR', 'MLT', 'MTK', 'MTQ', 'MTR', 'DAY', 'MON', 'HUR', 'KWH', 'SET', 'PR', 'BX', 'BG', 'CT', 'PF']
				const options = codes.map(code => ({ value: code, text: this.unitLabel(code) }))

				options.sort((a, b) => a.text.localeCompare(b.text))

				return options
			}
		},

		watch: {
			/**
			 * Le sélecteur ouvert pour les lignes cochées se referme si plus aucune ne l'est : choisir
			 * un cheval n'affecterait alors rien, sans le dire.
			 */
			selected_lines(lines) {
				if(!lines.length && this.horse_target === 'selection') this.horse_target = null
			}
		},

		methods: {
			/**
			 * Charge les articles du fournisseur, les lignes de la facture, puis son contrôle.
			 *
			 * Les articles viennent en premier : ce sont eux que la saisie des libellés propose.
			 */
			async init_component() {
				this.loaded = false

				this.articles = await this.getInvoiceSupplierArticles(this.invoicesupplier_id) || []

				await this.loadSavedLines(await this.getInvoiceSupplierDetails(this.invoicesupplier_id) || [])
				await this.refreshControl()

				this.loaded = true

				// L'analyse se charge après coup : l'écran est utilisable sans elle.
				this.initAI()
			},

			/**
			 * Repart des lignes en base : elles deviennent la version affichée, et les autres versions sont
			 * reconstruites à partir d'elles.
			 *
			 * @param {Array} lines Lignes telles que le serveur les rend.
			 */
			async loadSavedLines(lines) {
				this.saved_lines = lines
				this.saved_origin = this.savedOrigin(lines)
				this.date_edit = null
				this.drafts = {}
				this.signatures = {}
				this.balances = {}

				// La lecture optique ne se propose à part qu'une fois les lignes enregistrées : avant, elle
				// est déjà à l'écran.
				if(this.saved_origin === 'manual' && !this.vendor_lines) {
					this.ocr_lines = await this.getInvoiceSupplierOCRReading(this.invoicesupplier_id) || []
					this.storeVersion('ocr')
				}

				if(this.ai.task) this.storeVersion('ai')

				this.source = 'saved'
				this.lines = this.storeVersion('saved')

				Object.keys(this.drafts).filter(key => key !== 'saved').forEach(key => this.checkVersion(key))
			},

			/**
			 * Construit les lignes d'une version telle qu'elle a été lue, et retient leur empreinte.
			 *
			 * @param {String} key 'saved', 'ocr' ou 'ai'
			 * @return {Array} Lignes prêtes à être éditées.
			 */
			storeVersion(key) {
				let lines

				if(key === 'saved') lines = this.saved_lines.map(line => this.prepareLine(line))
				else if(key === 'ocr') lines = this.ocr_lines.map(line => this.prepareLine(line))
				else lines = this.aiLines(this.ai.task.aitask_result)

				this.$set(this.drafts, key, lines)
				this.$set(this.signatures, key, this.signature(lines))

				return lines
			},

			/**
			 * Lignes de l'analyse, prêtes à être éditées.
			 *
			 * Sur une facture électronique, l'analyse n'apporte que ce que le vendeur n'a pas déclaré : le
			 * cheval et la date, reportés sur ses lignes sans toucher au reste. Ailleurs, ses lignes
			 * remplacent celles de la lecture du document.
			 *
			 * @param {Object} result Résultat de l'analyse.
			 * @return {Array}
			 */
			aiLines(result) {
				if(result.vendor_lines) {
					const read_by_line = {}

					result.lines.filter(read => read.vendor_line).forEach(read => { read_by_line[read.vendor_line] = read })

					return this.saved_lines.map(line => {
						const read = read_by_line[line.isdetails_id]

						if(!read) return this.prepareLine(line)

						// Ce que l'utilisateur a déjà retenu sur la ligne — son cheval, sa date — prime sur ce
						// que l'analyse en lit.
						const dated = {
							...line,
							isdetails_start_date: line.isdetails_start_date || read.isdetails_start_date,
							isdetails_end_date: line.isdetails_end_date || read.isdetails_end_date
						}

						if(line.isdetails_horse) return this.prepareLine(dated)

						return this.prepareLine({
							...dated,
							isdetails_horse: read.isdetails_horse,
							isdetails_rebillable: !!read.isdetails_horse,
							horse_read: read.horse_read,
							horse_match: read.horse_match
						})
					})
				}

				return result.lines.map(line => this.prepareLine({
					...line,
					isdetails_id: null,
					isdetails_ht_src: null,
					isdetails_article: null,
					// Un cheval retrouvé par l'analyse se refacture d'office, comme un cheval affecté à la
					// main ; l'utilisateur décoche les exceptions.
					isdetails_rebillable: !!line.isdetails_horse,
					isdetails_origin: 'ai'
				}))
			},

			/**
			 * Empreinte de lignes, telles qu'elles seraient déposées.
			 */
			signature(lines) {
				return JSON.stringify(lines.map(line => this.linePayload(line)))
			},

			/**
			 * Résumé d'une version pour sa carte.
			 *
			 * @param {String} key Version.
			 * @param {String} title Suffixe du libellé de la carte.
			 */
			version(key, title) {
				const lines = this.versionLines(key)
				let summary = null

				if(lines) {
					summary = {
						count: lines.length,
						balanced: this.balances[key] === undefined ? null : this.balances[key],
						horses: lines.filter(line => line.isdetails_horse).length,
						dated: lines.filter(line => line.isdetails_start_date).length
					}
				}

				return {
					key,
					title: this.$t('invoice.supplier_details_version_' + title),
					dirty: this.isDirty(key),
					summary
				}
			},

			/**
			 * Lignes d'une version : celles à l'écran pour la version affichée, son brouillon sinon.
			 */
			versionLines(key) {
				return key === this.source ? this.lines : this.drafts[key]
			},

			/**
			 * Dit si une version a été retouchée depuis son chargement.
			 */
			isDirty(key) {
				const lines = this.versionLines(key)

				return !!lines && this.signatures[key] !== undefined && this.signature(lines) !== this.signatures[key]
			},

			/**
			 * Bascule le tableau sur une autre version du détail. Les retouches de la version quittée
			 * restent dans son brouillon.
			 *
			 * @param {String} key Version à afficher.
			 */
			showVersion(key) {
				if(key === this.source || !this.drafts[key]) return

				this.$set(this.drafts, this.source, this.lines)
				// Une saisie de date ouverte viserait les lignes de la version quittée.
				this.date_edit = null
				this.horse_target = null
				this.source = key
				this.lines = this.drafts[key]
				this.clearSelection()
				this.refreshControl()
			},

			/**
			 * Juge l'équilibre d'une version qui n'est pas affichée, pour sa carte.
			 */
			async checkVersion(key) {
				const control = await this.checkInvoiceSupplierDetailsSum(this.invoicesupplier_id, this.drafts[key].map(line => this.linePayload(line)))

				if(control) this.$set(this.balances, key, !!(control.ht && control.vat))
			},

			/**
			 * Charge les fournisseurs connectés et la dernière analyse de la facture, et reprend le suivi
			 * d'une analyse encore en cours.
			 */
			async initAI() {
				const [providers, tasks] = await Promise.all([
					this.getAIProviders(AI_TYPE),
					this.getAITasks(AI_TYPE, this.invoicesupplier_id).then(tasks => tasks || [])
				])

				if(providers) {
					this.ai.providers = providers.providers
					this.ai.last = providers.last
				}

				const done = tasks.find(task => task.aitask_status === 'done')
				const active = tasks.find(task => ['queued', 'running'].includes(task.aitask_status))
				const latest = tasks[0]

				if(done) this.applyAITask(done, false)
				if(latest && latest.aitask_status === 'failed') this.ai.failed = latest
				if(active) this.watchAITask(active)
			},

			/**
			 * Lance l'analyse avec le fournisseur choisi. Une analyse déjà faite sur ce document avec ce
			 * fournisseur revient aussitôt, sans nouvel appel.
			 *
			 * @param {String} provider
			 * @param {Boolean} thorough Relire avec le modèle plus attentif du fournisseur.
			 */
			async launchAI(provider, thorough = false) {
				const task = await this.launchInvoiceSupplierAI(this.invoicesupplier_id, provider, thorough)

				if(!task) return

				this.ai.last = provider
				this.ai.failed = null
				this.watchAITask(task)
			},

			/**
			 * Relance l'analyse en échec, sans refaire ce qui avait abouti.
			 */
			async retryAI() {
				const task = await this.retryAITask(this.ai.failed.aitask_id)

				if(!task) return

				this.ai.failed = null
				this.watchAITask(task)
			},

			/**
			 * Suit une analyse jusqu'à son terme. Le serveur ne prévient pas : l'écran le relève à
			 * intervalle régulier, tant qu'il est ouvert.
			 */
			watchAITask(task) {
				this.stopAIPolling()

				if(!['queued', 'running'].includes(task.aitask_status)) {
					this.onAITaskEnded(task)
					return
				}

				this.ai.running = task
				this.ai_poll = setInterval(async () => {
					const current = await this.getAITask(task.aitask_id)

					if(!current) return
					if(['queued', 'running'].includes(current.aitask_status)) {
						this.ai.running = current
						return
					}

					this.stopAIPolling()
					this.onAITaskEnded(current)
				}, AI_POLL_MS)
			},

			/**
			 * Arrête la relève de l'analyse en cours.
			 */
			stopAIPolling() {
				if(this.ai_poll) clearInterval(this.ai_poll)
				this.ai_poll = null
			},

			/**
			 * Une analyse vient de s'achever. Si l'utilisateur retouchait l'analyse précédente, la nouvelle
			 * attend qu'il la demande ; sinon elle s'affiche.
			 */
			onAITaskEnded(task) {
				this.ai.running = null

				if(task.aitask_status === 'failed') {
					this.ai.failed = task
					return
				}

				if(this.isDirty('ai')) this.ai.pending = task
				else this.applyAITask(task, true)
			},

			/**
			 * Retient une analyse comme celle de la carte « Analyse IA », et l'affiche si demandé.
			 *
			 * @param {Object} task Analyse aboutie.
			 * @param {Boolean} show Basculer le tableau dessus.
			 */
			applyAITask(task, show) {
				this.ai.task = task
				this.ai.pending = null

				const lines = this.storeVersion('ai')

				if(this.source === 'ai') {
					this.lines = lines
					this.refreshControl()
				}
				else {
					this.checkVersion('ai')
					if(show) this.showVersion('ai')
				}
			},

			/**
			 * Réponse à la question des prix toutes taxes comprises. Convertir ramène prix unitaires et
			 * montants au hors taxes du seul taux de la facture ; garder les laisse tels que lus.
			 *
			 * @param {Boolean} convert
			 */
			answerTTC(convert) {
				const rate = this.ttc_rate

				this.$set(this.ttc_answers, this.ai.task.aitask_id, convert)

				if(!convert) return

				const factor = 1 + rate / 100

				this.lines.filter(line => !line.locked).forEach(line => {
					const ht = this.toNumberOrNull(line.isdetails_ht)
					const htunit = this.toNumberOrNull(line.isdetails_htunit)

					if(ht !== null) line.isdetails_ht = Math.round(ht / factor * 100) / 100
					if(htunit !== null) line.isdetails_htunit = String(Math.round(htunit / factor * 10000) / 10000)
				})

				this.refreshControl()
			},

			/**
			 * Rattache à un cheval les lignes d'un cheval lu sur la facture : elles sont cochées, et le
			 * sélecteur s'ouvre pour elles.
			 */
			attachGroup(group) {
				this.clearSelection()
				this.selectGroup(group, true)
				this.horse_target = 'selection'
			},

			/**
			 * Range parmi les lignes sans cheval celles d'un cheval lu sur la facture.
			 */
			leaveGroup(group) {
				group.lines.forEach(line => { line.horse_read = null })
			},

			/**
			 * Replie ou déplie les lignes d'un intercalaire.
			 */
			toggleGroup(group) {
				this.$set(this.collapsed_groups, group.key, !group.collapsed)
			},

			/**
			 * Replie ou déplie tous les intercalaires.
			 */
			setGroupsCollapsed(collapsed) {
				this.collapsed_groups = collapsed ? Object.fromEntries(this.horse_groups.map(group => [group.key, true])) : {}
			},

			/**
			 * Ouvre la saisie de date, sous une ligne ou pour les lignes cochées.
			 *
			 * @param {Array} lines Lignes à dater.
			 * @param {Object|null} line Ligne sous laquelle la saisie s'ouvre, null pour la sélection.
			 */
			openDateEditor(lines, line) {
				this.date_edit = { lines, line, opened_at: Date.now() }
			},

			/**
			 * Pose une date d'acte ou une période sur des lignes ; des dates vides les en retirent.
			 *
			 * @param {Array} lines
			 * @param {Object} range { start, end }, chacun au format AAAA-MM-JJ ou null.
			 */
			applyDates(lines, range) {
				lines.forEach(line => {
					line.isdetails_start_date = range.start
					line.isdetails_end_date = range.end
				})

				if(!this.date_edit.line) this.clearSelection()
				this.date_edit = null
			},

			/**
			 * Libellé de la pastille de date d'une ligne.
			 */
			dateLabel(line) {
				const start = line.isdetails_start_date
				const end = line.isdetails_end_date

				if(!start) return this.$t('invoice.supplier_details_date_add')
				if(!end || end === start) return this.$t('invoice.supplier_details_date_act', { date: this.shortDate(start) })

				return this.$t('invoice.supplier_details_date_period', { start: this.shortDate(start), end: this.shortDate(end) })
			},

			/**
			 * Date AAAA-MM-JJ écrite dans la langue de l'écran.
			 */
			shortDate(value) {
				const [year, month, day] = value.split('-').map(Number)

				return new Date(year, month - 1, day).toLocaleDateString(this.number_locale)
			},

			/**
			 * Écart entre une quantité au prorata et la période de la ligne : 17/30 sur une période de
			 * trente et un jours dit qu'une des deux est fausse. Un simple signal, la quantité n'est pas
			 * recalculée.
			 *
			 * @return {String|null} Le message, ou null si les deux s'accordent ou ne se comparent pas.
			 */
			prorataMismatch(line) {
				const start = line.isdetails_start_date
				const end = line.isdetails_end_date
				const quantity = this.splitQuantity(line.qty_input)

				// Seule une quantité écrite en prorata se compare à la période.
				if(!start || !end || end === start || !String(line.qty_input).includes('/')) return null

				const [y1, m1, d1] = start.split('-').map(Number)
				const [y2, m2, d2] = end.split('-').map(Number)
				const days = Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000) + 1

				return this.toNumberOrNull(quantity.qte) === days ? null : this.$t('invoice.supplier_details_date_prorata_mismatch', { days, qty: line.qty_input })
			},

			/**
			 * Identifiants lus sur la facture pour un cheval inconnu — transpondeur, SIRE —, pour aider à
			 * le reconnaître ou à le créer.
			 */
			readHorseIds(read) {
				return [
					read.transponder ? this.$t('invoice.supplier_details_horse_read_transponder', { value: read.transponder }) : null,
					read.sire ? this.$t('invoice.supplier_details_horse_sire', { sire: read.sire }) : null
				].filter(id => id).join(' · ')
			},

			/**
			 * Ouvre la création du cheval lu sur la facture pour un intercalaire.
			 */
			openHorseCreation(group) {
				this.horse_creation_read = group.lines[0].horse_read
				this.$refs.horseCreationModal.show()
			},

			/**
			 * Le cheval lu vient d'être créé, ou retrouvé dans la licence : il est affecté à toutes les lignes
			 * qui le désignaient, dans chaque version — retouches comprises. Un cheval que l'utilisateur a
			 * déjà affecté ou écarté n'est pas touché.
			 *
			 * La saisie manuelle crée le cheval en local : il n'a d'identifiant serveur, celui que les lignes
			 * enregistrent, qu'une fois synchronisé.
			 *
			 * @param {Number} horse_id Cheval créé
			 */
			async onHorseCreated(horse_id) {
				this.$refs.horseCreationModal.hide()

				const horse = await this.getHorseById(horse_id)

				if(horse_id < 0) {
					await this.$sync.force(false, false)
					horse.horse_id = this.$sync.replaceWithReplicated('horse', horse_id)
				}

				const key = JSON.stringify(this.horse_creation_read)
				const lines = [...new Set([...this.lines, ...Object.values(this.drafts).flat()])]

				this.assignHorse(lines.filter(line => !line.isdetails_horse && JSON.stringify(line.horse_read) === key), horse)
			},

			/**
			 * Nom du cheval lu sur la facture pour une ligne qui n'en porte aucun, ou null.
			 */
			readHorseName(line) {
				if(line.isdetails_horse || !line.horse_read) return null

				return line.horse_read.name || line.horse_read.sire || line.horse_read.transponder || null
			},

			/**
			 * Met une ligne en état d'être éditée, et retient si elle est verrouillée. Une ligne lue
			 * optiquement et rangée sous un article nommé autrement prend le nom de cet article.
			 */
			prepareLine(line) {
				const locked = line.isdetails_origin === 'xml'

				// Une ligne modifiable dont la quantité n'a pas été lue part sur une unité, comme elle part
				// sur l'unité neutre : c'est le cas de loin le plus courant, et l'utilisateur la corrige au
				// moment où il recontrôle la ligne. Rien n'est écrit en base avant son enregistrement.
				// La base rend un décimal à cinq chiffres : sans cela le champ afficherait 2.00000.
				const qte = this.trimDecimals(line.isdetails_qte)
				const base = this.trimDecimals(line.isdetails_base_qty)
				let qty_input = qte === null && !locked ? '1' : qte

				if(qty_input !== null && base !== null && base !== '1') {
					qty_input += '/' + base
				}

				const prepared = {
					...line,
					isdetails_rebillable: !!line.isdetails_rebillable,
					// Une ligne qui n'annonce aucune unité part sur l'unité neutre. Le serveur écarte ce
					// que la lecture optique lui donne et qu'il ne reconnaît pas, si bien qu'une unité
					// absente ne se distingue pas d'une unité mal lue : une case vide n'apprend rien à qui
					// recontrôle la facture, et c'est le moment où il la corrige. Celle du vendeur est
					// laissée telle quelle — son document la déclare, elle ne se déduit pas ici.
					isdetails_unit: locked ? line.isdetails_unit : line.isdetails_unit || 'C62',
					// L'écran corrige la ligne en regard du PDF : le montant s'y lit donc dans la devise du
					// document, là où la clé ordinaire porte sa contrepartie comptable. Hors devise
					// étrangère, les deux se confondent et rien ne change.
					isdetails_ht: line.isdetails_ht_src === null ? line.isdetails_ht : line.isdetails_ht_src,
					// Quantité et quantité de base se saisissent ensemble, sous la forme d'un prorata dès
					// que la seconde n'est pas l'unité.
					qty_input,
					isdetails_htunit: this.trimDecimals(line.isdetails_htunit),
					isdetails_discount: this.trimDecimals(line.isdetails_discount),
					// Une ligne sans remise propose le pourcentage, la forme la plus courante.
					isdetails_discount_type: line.isdetails_discount_type || 'percent',
					// Une ligne modifiable sans quote-part relevée est facturée en entier : la case part sur
					// cent pour cent, valeur que l'enregistrement dépose telle quelle.
					isdetails_part: line.isdetails_part === null && !locked ? '100' : this.trimDecimals(line.isdetails_part),
					article_label: line.isdetails_article ? line.isdetails_article.isarticles_label : line.isdetails_label,
					article_renamed: !!line.isdetails_article && this.isOtherArticle(line.isdetails_article.isarticles_label, line.isdetails_label),
					// Libellé avant la dernière correction : les lignes qui le portaient encore suivent la
					// correction. Il n'existe qu'à l'écran.
					label_before: line.isdetails_label,
					// Ligne cochée pour l'affectation d'un cheval. Elle n'existe qu'à l'écran.
					selected: false,
					// Cheval tel que l'analyse l'a lu sur le document, et la clé par laquelle elle l'a
					// retrouvé chez vous. Ils n'existent qu'à l'écran.
					horse_read: line.horse_read || null,
					horse_match: line.horse_match || null,
					locked
				}

				// Une ligne lue optiquement que le serveur a rangée sous un article nommé autrement — par un
				// libellé déjà corrigé sur une facture précédente, ou par le code du vendeur — s'affiche sous
				// le nom de cet article, comme l'utilisateur l'aurait corrigée. Rien n'est écrit avant
				// l'enregistrement, et le serveur conserve à part le libellé lu sur le document.
				if(prepared.isdetails_origin === 'ocr' && prepared.article_renamed) {
					prepared.isdetails_label = prepared.article_label
					prepared.label_before = prepared.article_label
					prepared.article_renamed = false
				}

				return prepared
			},

			/**
			 * Ajoute une ligne vide au tableau. Elle n'existe qu'à l'écran jusqu'au dépôt de l'ensemble.
			 *
			 * Ajoutée depuis l'intercalaire d'un cheval, elle lui est rattachée d'emblée, et refacturable
			 * comme toute ligne qui reçoit un cheval.
			 *
			 * @param {Object|null} horse
			 */
			addLine(horse) {
				this.lines.push(this.prepareLine({
					isdetails_id: null,
					isdetails_label: '',
					isdetails_qte: null,
					isdetails_base_qty: null,
					isdetails_unit: null,
					isdetails_htunit: null,
					isdetails_discount: null,
					isdetails_discount_type: null,
					isdetails_part: null,
					isdetails_ht: 0,
					isdetails_vat_percent: null,
					isdetails_start_date: null,
					isdetails_end_date: null,
					isdetails_horse: horse,
					isdetails_rebillable: !!horse,
					isdetails_origin: 'manual'
				}))
			},

			/**
			 * Le cheval vient d'être choisi dans le sélecteur : il est affecté aux lignes visées, que
			 * l'on décoche.
			 */
			onHorsePicked(horse) {
				this.assignHorse(this.horse_target === 'all' ? this.lines : this.selected_lines, horse)

				this.horse_target = null
				this.clearSelection()
			},

			/**
			 * Rattache des lignes à un cheval.
			 *
			 * Une ligne qui change de cheval devient refacturable : c'est le cas courant, la pension ou
			 * les soins d'un cheval se refacturent à son propriétaire, et l'utilisateur décoche les
			 * exceptions. Une ligne qui garde son cheval garde aussi son choix.
			 */
			assignHorse(lines, horse) {
				lines.forEach(line => {
					if(!line.isdetails_horse || line.isdetails_horse.horse_id !== horse.horse_id) {
						line.isdetails_rebillable = true
						line.horse_match = null
					}

					line.isdetails_horse = horse
				})
			},

			/**
			 * Détache des lignes de leur cheval. Sans cheval, il n'y a plus personne à qui les refacturer.
			 */
			unassignHorse(lines) {
				lines.forEach(line => {
					line.isdetails_horse = null
					line.isdetails_rebillable = false
					line.selected = false
				})
			},

			/**
			 * Coche ou décoche toutes les lignes d'un intercalaire.
			 */
			selectGroup(group, selected) {
				group.lines.forEach(line => line.selected = selected)
			},

			/**
			 * Rend refacturables, ou non, toutes les lignes d'un intercalaire.
			 */
			setGroupRebillable(group, rebillable) {
				group.lines.forEach(line => line.isdetails_rebillable = rebillable)
			},

			/**
			 * Décoche toutes les lignes, une fois le cheval affecté ou à la demande de l'utilisateur.
			 */
			clearSelection() {
				this.lines.forEach(line => line.selected = false)
			},

			/**
			 * Total hors taxes de lignes, dans la devise du document.
			 *
			 * Cumulé en centimes exacts puis formaté une seule fois : additionner des montants en virgule
			 * flottante perd un centime sur une facture de pension. Le formatage reste synchrone, le
			 * total étant recalculé à chaque saisie dans les intercalaires.
			 */
			totalLabel(lines) {
				let cents = BigInt(0)

				lines.forEach(line => {
					const amount = this.toRational(line.isdetails_ht)

					if(amount !== null) cents += this.roundCents({ num: amount.num * BigInt(100), den: amount.den })
				})

				return (Number(cents) / 100).toLocaleString(this.number_locale, { style: 'currency', currency: this.currency })
			},

			/**
			 * Montant relevé par l'analyse, dans la devise du document.
			 *
			 * @param {Number} value
			 * @return {String}
			 */
			amountLabel(value) {
				return value.toLocaleString(this.number_locale, { style: 'currency', currency: this.currency })
			},

			/**
			 * Ce que la ligne enverra au serveur, dans un ordre stable.
			 */
			linePayload(line) {
				const quantity = this.splitQuantity(line.qty_input)
				const discount = this.toNumberOrNull(line.isdetails_discount)

				return {
					isdetails_id: line.isdetails_id,
					isdetails_label: line.isdetails_label,
					// Libellé lu sur le document, conservé par le serveur sur une ligne nouvelle — celle d'une
					// lecture affichée à l'écran.
					isdetails_label_read: line.isdetails_label_read || null,
					isdetails_article_label: line.article_label,
					isdetails_qte: this.toNumberOrNull(quantity.qte),
					// Une base illisible ou nulle ne divise rien : le prix s'entend alors pour une unité.
					isdetails_base_qty: this.toNumberOrNull(quantity.base) || 1,
					isdetails_unit: line.isdetails_unit,
					isdetails_htunit: this.toNumberOrNull(line.isdetails_htunit),
					isdetails_discount: discount,
					isdetails_discount_type: discount === null ? null : line.isdetails_discount_type,
					isdetails_part: this.toNumberOrNull(line.isdetails_part),
					isdetails_ht: this.toNumberOrNull(line.isdetails_ht) || 0,
					isdetails_vat_percent: this.toNumberOrNull(line.isdetails_vat_percent),
					// Une date d'acte est une période d'un jour : début et fin égaux.
					isdetails_start_date: line.isdetails_start_date,
					isdetails_end_date: line.isdetails_end_date,
					isdetails_horse: line.isdetails_horse ? line.isdetails_horse.horse_id : null,
					isdetails_rebillable: line.isdetails_rebillable
				}
			},

			/**
			 * Dépose l'ensemble des lignes.
			 *
			 * Le bouton ne s'ouvre que lorsque les totaux retombent sur ceux de la facture : déposer un
			 * détail qui ne se réconcilie pas reviendrait à installer en base une incohérence que la
			 * ventilation comptable et l'export auraient ensuite à porter. L'écran travaille donc en
			 * local jusque-là, et n'écrit qu'une fois.
			 */
			async saveLines() {
				this.saving = true

				const saved = await this.saveInvoiceSupplierDetails(
					this.invoicesupplier_id,
					this.lines.map(line => this.linePayload(line)),
					this.source === 'ai' ? this.ai.task.aitask_id : null
				)

				this.saving = false

				if(!saved) {
					this.failureToast("toast.info_save_failed")
					return
				}

				await this.loadSavedLines(saved)
				await this.refreshControl()

				this.successToast("toast.info_modif_succes")
				this.$emit('invoice-updated')
			},

			/**
			 * Le nom d'article vient d'être choisi dans la liste ou créé : il cesse de suivre le libellé
			 * de la ligne.
			 *
			 * Choisir un article existant et en nommer un nouveau passent par le même chemin : le nom tapé
			 * dans la recherche devient celui de l'article, comme s'il avait été pris parmi les autres.
			 *
			 * Repasser le nom d'article à celui de la ligne les réunit à nouveau, plutôt que de figer un
			 * lien rompu sur une égalité retrouvée par hasard.
			 *
			 * Le choix vaut pour toutes les lignes au même libellé, et il est retenu dans chosen_articles :
			 * un libellé corrigé ensuite vers celui-ci y tombe d'emblée, comme le serveur le fera à
			 * l'enregistrement.
			 *
			 * @param {Object} line Ligne dont l'article est choisi.
			 * @param {String} label Nom de l'article choisi ou créé.
			 */
			onArticleChosen(line, label) {
				const normalized = this.normalizeLabel(line.isdetails_label)
				const renamed = this.isOtherArticle(label, line.isdetails_label)

				// Les autres lignes au même libellé suivent : c'est ce que le serveur retiendra du choix,
				// et ce qu'il appliquera aux factures suivantes du fournisseur.
				this.lines.filter(other => this.normalizeLabel(other.isdetails_label) === normalized).forEach(other => {
					other.article_label = label
					other.article_renamed = renamed
				})

				this.$set(this.chosen_articles, normalized, renamed ? label : null)
			},

			/**
			 * Le libellé d'une ligne vient d'être corrigé : les lignes qui portaient le même le reçoivent,
			 * et chacune se range sous l'article de son libellé.
			 *
			 * @param {Object} line
			 */
			onLabelChanged(line) {
				const previous = this.normalizeLabel(line.label_before)

				// Une facture de pension répète le même libellé pour chaque cheval : corriger la première
				// ligne corrige les suivantes. Les lignes du vendeur gardent leur libellé, qui fait foi,
				// mais se rangent sous le même article.
				if(previous !== '' && previous !== this.normalizeLabel(line.isdetails_label)) {
					this.lines.filter(other => other !== line && this.normalizeLabel(other.isdetails_label) === previous).forEach(other => {
						if(other.locked) {
							this.onArticleChosen(other, line.isdetails_label)
							return
						}

						other.isdetails_label = line.isdetails_label
						this.applyLabelArticle(other)
					})
				}

				this.applyLabelArticle(line)
			},

			/**
			 * Range la ligne sous l'article de son libellé : celui sous lequel ce libellé a déjà été rangé
			 * (article_by_label), à défaut le libellé lui-même, tant que l'article n'en a pas été distingué.
			 * Le libellé devient aussi le point de départ de la prochaine correction (label_before).
			 *
			 * C'est le comportement attendu par défaut : on corrige un libellé mal lu, et l'article se
			 * range sous ce nom. Dès que l'utilisateur a nommé l'article autrement — pour rapprocher
			 * deux factures mensuelles dont le vendeur date le libellé, par exemple — ce nom lui
			 * appartient, et corriger la ligne ne le défait plus — sauf si la correction ramène la ligne au
			 * nom de son article : les deux se rejoignent, et le libellé cesse de l'annoncer comme distinct.
			 *
			 * @param {Object} line
			 */
			applyLabelArticle(line) {
				line.label_before = line.isdetails_label

				if(line.article_renamed) {
					line.article_renamed = this.isOtherArticle(line.article_label, line.isdetails_label)
				}
				else {
					// Un libellé déjà rangé sous un autre article y tombe d'emblée, comme le ferait le
					// serveur à l'enregistrement.
					line.article_label = this.article_by_label[this.normalizeLabel(line.isdetails_label)] || line.isdetails_label
					line.article_renamed = this.isOtherArticle(line.article_label, line.isdetails_label)
				}
			},

			/**
			 * Dit si un nom d'article désigne autre chose que le libellé de la ligne.
			 *
			 * La comparaison est celle du serveur (Helper::normalizeLabel), qui retrouve un article sans
			 * égard à la casse, aux accents ni à la ponctuation : « PENSION BOX » se range sous l'article
			 * « Pension box ». Comparés à la lettre, ces deux libellés feraient annoncer au-dessus de la
			 * case un article qui n'est que la ligne elle-même, écrite autrement.
			 */
			isOtherArticle(article_label, line_label) {
				return this.normalizeLabel(article_label) !== this.normalizeLabel(line_label)
			},

			/**
			 * Forme comparable d'un libellé : minuscules sans accents, et tout ce qui n'est ni lettre ni
			 * chiffre ramené à un espace, comme le fait le serveur.
			 */
			normalizeLabel(label) {
				return this.accentsTidy(label).replace(/[^a-z0-9]+/g, ' ').trim()
			},

			/**
			 * Origine d'un détail tel que le serveur le rend : 'ocr' s'il reste une ligne que seule la
			 * lecture optique a posée, 'manual' si toutes ont été enregistrées, null sans ligne. Les lignes
			 * du vendeur ne comptent pas : elles ont leur propre mention.
			 */
			savedOrigin(lines) {
				if(lines.some(line => line.isdetails_origin === 'ocr')) return 'ocr'
				if(lines.some(line => ['manual', 'ai'].includes(line.isdetails_origin))) return 'manual'

				return null
			},

			/**
			 * Recalcule le montant hors taxes en quittant l'un de ses facteurs : quantité, quote-part, prix
			 * unitaire ou remise.
			 *
			 * Le déclencheur est la sortie du champ, et non sa modification : la lecture optique se
			 * trompe sur une partie des lignes, et le geste de contrôle consiste justement à parcourir
			 * les facteurs au clavier. Exiger une réécriture laisserait en place un montant faux dont les
			 * facteurs sont pourtant justes.
			 *
			 * Un montant qui retombe déjà sur ses facteurs à moins d'un centime est laissé tel quel : c'est
			 * celui du document, et les vendeurs n'arrondissent pas tous pareil — Groomy tronque une pension
			 * à 82,875 en 82,87 là où l'arrondi donnerait 82,88. Le réécrire déséquilibrerait la facture
			 * d'un centime.
			 */
			onFactorLeft(line) {
				const expected = this.expectedCents(line)
				const valid = ['qty', 'part', 'discount'].every(factor => this.factorState(line, factor) !== false)

				// Une case en défaut ne réécrit rien : le montant tiré d'une faute de frappe effacerait
				// celui du document.
				if(expected !== null && valid && !this.isWithinACent(line.isdetails_ht, expected)) {
					line.isdetails_ht = (Number(this.roundCents(expected)) / 100).toFixed(2)
				}

				this.refreshControl()
			},

			/**
			 * Lit la forme de la remise sur ce qui suit le nombre : « 10 % », « 5 €/u » ou « 5 € » la fixent
			 * sans passer par la liste, puis le montant de la ligne est repris. La devise s'écrit par son
			 * symbole ou par son code (« 5 EUR/u »), sans égard à la casse ; une saisie que rien ne
			 * reconnaît reste telle quelle, et sa case passe en rouge.
			 */
			onDiscountLeft(line) {
				if(line.isdetails_discount !== null) {
					let text = String(line.isdetails_discount).trim()
					let type = null

					if(text.endsWith('%')) {
						type = 'percent'
						text = text.slice(0, -1)
					}
					else {
						const per_unit = /\/u$/i.test(text)

						if(per_unit) text = text.slice(0, -2).trim()

						const sign = [this.currency_symbol, this.currency].find(candidate => text.toUpperCase().endsWith(candidate.toUpperCase()))

						if(sign) text = text.slice(0, -sign.length)
						if(sign || per_unit) type = per_unit ? 'amount' : 'line'
					}

					if(type) {
						line.isdetails_discount_type = type
						line.isdetails_discount = text.trim()
					}
				}

				this.onFactorLeft(line)
			},

			/**
			 * La quote-part se saisit volontiers suivie de son signe, « 50 % » : il est retiré, la case
			 * l'affiche déjà à côté.
			 */
			onPartLeft(line) {
				line.isdetails_part = this.withoutPercent(line.isdetails_part)

				this.onFactorLeft(line)
			},

			/**
			 * Le taux se saisit volontiers suivi de son signe, « 20 % » : le signe est retiré, la case
			 * l'affiche déjà à côté.
			 */
			onRateChanged(line) {
				line.isdetails_vat_percent = this.withoutPercent(line.isdetails_vat_percent)

				this.refreshControl()
			},

			/**
			 * Une saisie de pourcentage débarrassée de son signe.
			 */
			withoutPercent(value) {
				if(value === null) return null

				return String(value).replace('%', '').trim()
			},

			/**
			 * Change l'unité d'une ligne. Elle n'entre pas dans le montant, mais le contrôle se rejoue à
			 * chaque geste.
			 */
			setUnit(line, unit) {
				line.isdetails_unit = unit

				this.refreshControl()
			},

			/**
			 * Change la forme de la remise d'une ligne, puis reprend son montant.
			 */
			setDiscountType(line, type) {
				line.isdetails_discount_type = type

				this.onFactorLeft(line)
			},

			/**
			 * Signe d'une forme de remise : %, montant par unité ou montant sur la ligne.
			 */
			discountUnit(type) {
				if(type === 'amount') return this.currency_symbol + '/u'
				if(type === 'line') return this.currency_symbol

				return '%'
			},

			/**
			 * Remise d'une ligne suivie de sa forme, pour l'affichage en lecture seule et dans le calcul.
			 */
			discountLabel(line) {
				if(line.isdetails_discount === null) return ''

				return line.isdetails_discount + ' ' + this.discountUnit(line.isdetails_discount_type)
			},

			/**
			 * Pourcentage suivi de son signe — quote-part ou taux —, vide quand il n'est pas renseigné.
			 */
			percentLabel(value) {
				if(value === null) return ''

				return value + ' %'
			},

			/**
			 * État d'une case de facteur : rouge si elle est illisible ou hors de ses bornes, neutre sinon.
			 *
			 * Une case vide est neutre : le facteur n'a pas été lu, la ligne est incomplète. Les bornes sont
			 * celles du serveur — quatre décimales au plus, quote-part entre 0 et 100, remise positive et
			 * jamais au-delà de 100 % —, auxquelles s'ajoute une remise qui ferait changer de signe le
			 * montant de la ligne : c'est presque toujours une faute de frappe.
			 *
			 * @param {Object} line
			 * @param {String} factor 'qty', 'part' ou 'discount'.
			 * @return {Boolean|null} false si la case est en défaut, null sinon.
			 */
			factorState(line, factor) {
				if(factor === 'qty') {
					const text = line.qty_input === null ? '' : String(line.qty_input).trim()

					if(text === '') return null

					const parts = text.split('/')
					const qte = this.toRational(parts[0])
					const base = parts.length === 2 ? this.toRational(parts[1]) : { num: BigInt(1) }

					return parts.length > 2 || qte === null || base === null || base.num <= BigInt(0) ? false : null
				}

				const value = factor === 'part' ? line.isdetails_part : line.isdetails_discount

				if(value === null || String(value).trim() === '') return null

				const decimal = this.parseDecimal(value)

				if(decimal === null || decimal.scale > 4 || decimal.value < BigInt(0)) return false

				const rational = this.toRational(value)

				if(factor === 'part') {
					return rational.num === BigInt(0) || rational.num > BigInt(100) * rational.den ? false : null
				}

				if(line.isdetails_discount_type === 'percent' && rational.num > BigInt(100) * rational.den) return false

				const before = this.expectedCents({ ...line, isdetails_discount: null })
				const after = this.expectedCents(line)

				if(before === null || after === null) return null

				return before.num !== BigInt(0) && after.num !== BigInt(0) && (before.num < BigInt(0)) !== (after.num < BigInt(0)) ? false : null
			},

			/**
			 * Calcul qui mène au montant d'une ligne, écrit en clair dans l'ordre des cases :
			 * 17/30 × 50 % × (325 − 10 %) = 82,875, ou (2 × 50 % × 100) − 10 € = 90.
			 *
			 * Les facteurs sont repris tels qu'ils sont saisis. Une quote-part pleine est tue : elle ne
			 * change rien et chargerait chaque ligne. Rien n'est rendu tant qu'un facteur manque.
			 *
			 * Une remise sur la ligne se retranche du produit entier, une seule fois : le produit est mis
			 * entre parenthèses pour qu'on ne la lise pas comme retranchée du seul prix unitaire.
			 */
			/**
			 * Écart du tarif de la ligne avec le dernier constaté pour son article sur une facture
			 * antérieure, quand il dépasse 10 % dans un sens ou dans l'autre : une saisie erronée, ou
			 * deux articles différents rangés sous un même nom.
			 *
			 * @param {Object} line
			 * @return {Object|null} Paramètres du message, ou null s'il n'y a rien à signaler.
			 */
			priceDrift(line) {
				const previous = line.previous_price
				const price = this.toNumberOrNull(line.isdetails_htunit)

				// La référence vaut pour l'article enregistré : une ligne rangée sous un autre article à
				// l'écran se comparera au sien une fois enregistrée.
				if(!previous || !previous.price || price === null || line.article_label !== line.isdetails_article.isarticles_label) return null

				const percent = (price - previous.price) / previous.price * 100

				if(Math.abs(percent) <= 10) return null

				return {
					percent,
					variation: Math.abs(percent).toFixed(0),
					previous: previous.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
					invoice: previous.invoice_num || previous.invoice_id,
					date: this.formatDate(previous.date)
				}
			},

			formulaLabel(line) {
				const expected = this.expectedCents(line)

				if(expected === null) return ''

				const discounted = this.toRational(line.isdetails_discount) !== null
				const part = this.toNumberOrNull(line.isdetails_part)
				let formula = line.qty_input

				if(part !== null && part !== 100) {
					formula += ' × ' + this.percentLabel(line.isdetails_part)
				}

				if(discounted && line.isdetails_discount_type !== 'line') {
					formula += ' × (' + line.isdetails_htunit + ' − ' + this.discountLabel(line) + ')'
				}
				else {
					formula += ' × ' + line.isdetails_htunit
				}

				if(discounted && line.isdetails_discount_type === 'line') {
					formula = '(' + formula + ') − ' + this.discountLabel(line)
				}

				// Le résultat garde ses décimales au-delà du centime : un 82,875 que le vendeur a tronqué en
				// 82,87 explique de lui-même l'écart, là où un arrondi à 82,88 le ferait passer pour une erreur.
				const amount = Number(expected.num) / Number(expected.den) / 100

				return formula + ' = ' + amount.toLocaleString(this.number_locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
			},

			/**
			 * Sépare une quantité saisie en prorata — 17/30 — en quantité et quantité de base. Sans
			 * barre, la base est l'unité.
			 *
			 * Les deux restent des chaînes : le calcul du montant les relit telles qu'elles sont écrites.
			 *
			 * @return {Object} {qte: String|null, base: String}
			 */
			splitQuantity(text) {
				if(text === null || text === undefined) return { qte: null, base: '1' }

				const parts = String(text).split('/')

				return { qte: parts[0].trim(), base: parts.length > 1 ? parts[1].trim() : '1' }
			},

			/**
			 * Lit un nombre écrit en chiffres, avec une virgule ou un point décimal, et le rend sous forme
			 * d'entier accompagné du nombre de décimales qu'il portait.
			 *
			 * @return {Object|null} {value: BigInt, scale: Number}, ou null si ce n'est pas un nombre.
			 */
			parseDecimal(value) {
				if(value === null || value === undefined || value === '') return null

				const text = String(value).trim().replace(',', '.')

				if(!/^-?\d*\.?\d*$/.test(text) || !/\d/.test(text)) return null

				const negative = text.charAt(0) === '-'
				const parts = text.replace('-', '').split('.')
				const whole = parts[0] || '0'
				const fraction = parts[1] || ''
				const digits = BigInt(whole + fraction)

				return { value: negative ? -digits : digits, scale: fraction.length }
			},

			/**
			 * Dit si le montant d'une ligne contredit ses propres facteurs.
			 *
			 * C'est le signe le plus sûr d'une lecture optique fautive : sur les factures déjà lues,
			 * quantité fois prix unitaire ne retombe pas sur le total relevé une fois sur deux. Un écart de
			 * moins d'un centime n'en est pas un : il ne vient que de la façon dont le vendeur arrondit.
			 *
			 * Rien n'est signalé quand l'un des facteurs manque : une ligne dont le prix unitaire n'a pas
			 * été lu n'est pas incohérente, elle est incomplète.
			 */
			hasAmountMismatch(line) {
				const expected = this.expectedCents(line)

				if(expected === null || this.parseDecimal(line.isdetails_ht) === null) return false

				return !this.isWithinACent(line.isdetails_ht, expected)
			},

			/**
			 * Montant hors taxes qu'annoncent les facteurs d'une ligne, en centimes, sous forme de fraction
			 * exacte.
			 *
			 * Quantité × quote-part × (prix unitaire − remise par unité) − remise sur la ligne : le prix
			 * unitaire est remisé — en pourcentage, ou d'un montant par unité —, rapporté à la quantité de
			 * base, multiplié par la quantité puis réduit à la quote-part facturée. Une remise sur la ligne
			 * se retranche en dernier, une seule fois, du montant ainsi obtenu.
			 *
			 * Tout reste en entiers exacts jusqu'au bout, numérateur et dénominateur séparés : en virgule
			 * flottante, 3 × 0,1 vaut 0,30000000000000004, et un prorata de 17/30 n'a pas d'écriture décimale
			 * finie. L'arrondi, s'il y en a un, se fait une seule fois, par qui reçoit la fraction.
			 *
			 * @return {Object|null} {num: BigInt, den: BigInt} avec den > 0, ou null s'il manque un facteur.
			 */
			expectedCents(line) {
				const quantity = this.splitQuantity(line.qty_input)
				const qte = this.toRational(quantity.qte)
				const base = this.toRational(quantity.base)
				let price = this.toRational(line.isdetails_htunit)

				if(qte === null || base === null || price === null || base.num <= BigInt(0)) return null

				const discount = this.toRational(line.isdetails_discount)

				if(discount !== null && line.isdetails_discount_type === 'amount') {
					price = { num: price.num * discount.den - discount.num * price.den, den: price.den * discount.den }
				}
				else if(discount !== null && line.isdetails_discount_type === 'percent') {
					price = { num: price.num * (BigInt(100) * discount.den - discount.num), den: price.den * discount.den * BigInt(100) }
				}

				let num = qte.num * price.num * base.den * BigInt(100)
				let den = qte.den * price.den * base.num

				const part = this.toRational(line.isdetails_part)

				if(part !== null) {
					num = num * part.num
					den = den * part.den * BigInt(100)
				}

				if(discount !== null && line.isdetails_discount_type === 'line') {
					num = num * discount.den - discount.num * BigInt(100) * den
					den = den * discount.den
				}

				return { num, den }
			},

			/**
			 * Dit si un montant saisi retombe sur un montant attendu à moins d'un centime près.
			 *
			 * @param {String|Number} amount Montant en unités monétaires, tel qu'il est écrit.
			 * @param {Object} expected Fraction de centimes rendue par expectedCents.
			 */
			isWithinACent(amount, expected) {
				const written = this.toRational(amount)

				if(written === null) return false

				// Les deux fractions sont ramenées au même dénominateur : l'écart reste entier, et se
				// compare au centime sans division.
				const gap = written.num * BigInt(100) * expected.den - expected.num * written.den
				const cent = written.den * expected.den

				return gap < cent && -gap < cent
			},

			/**
			 * Arrondit une fraction de centimes au centime, au demi supérieur en valeur absolue — le signe
			 * est mis de côté pour qu'un avoir s'arrondisse comme une facture.
			 *
			 * @return {BigInt}
			 */
			roundCents(fraction) {
				const negative = fraction.num < BigInt(0)
				const absolute = negative ? -fraction.num : fraction.num
				const quotient = absolute / fraction.den
				const remainder = absolute % fraction.den
				const rounded = remainder * BigInt(2) >= fraction.den ? quotient + BigInt(1) : quotient

				return negative ? -rounded : rounded
			},

			/**
			 * Nombre écrit, sous forme de fraction exacte.
			 *
			 * @return {Object|null} {num: BigInt, den: BigInt}, ou null si ce n'est pas un nombre.
			 */
			toRational(value) {
				const decimal = this.parseDecimal(value)

				if(decimal === null) return null

				return { num: decimal.value, den: this.powerOfTen(decimal.scale) }
			},

			/**
			 * Puissance de dix, en entier exact.
			 *
			 * Écrite plutôt que calculée : l'opérateur d'exponentiation est réécrit en Math.pow à la
			 * compilation, et Math.pow refuse les entiers exacts.
			 *
			 * @param {Number} exponent
			 * @return {BigInt}
			 */
			powerOfTen(exponent) {
				return BigInt('1' + '0'.repeat(exponent))
			},

			/**
			 * Retire les zéros décimaux que la base ajoute à un décimal.
			 *
			 * @param {String|Number|null} value
			 * @return {String|null}
			 */
			trimDecimals(value) {
				if(value === null || value === undefined || value === '') return value

				const parsed = parseFloat(value)

				return isNaN(parsed) ? value : String(parsed)
			},

			/**
			 * Nombre attendu par le serveur, à partir d'une saisie qui peut porter une virgule décimale
			 * ou être vide.
			 *
			 * Il est lu comme le lit le calcul du montant : une saisie que celui-ci ignore — « 5 $ »,
			 * « 50 % » — ne doit pas partir en base sous une autre valeur que celle qu'il a comptée.
			 */
			toNumberOrNull(value) {
				const rational = this.toRational(value)

				if(rational === null) return null

				return Number(rational.num) / Number(rational.den)
			},

			/**
			 * Retire une ligne du tableau. Rien n'est perdu tant que l'ensemble n'a pas été déposé :
			 * quitter l'onglet sans enregistrer la ramène.
			 */
			removeLine(line) {
				this.lines = this.lines.filter(other => other !== line)

				this.refreshControl()
			},

			/**
			 * Reprend le contrôle des sommes sur l'état de l'écran, et prépare son affichage.
			 *
			 * Il est rejoué à chaque geste : c'est lui qui ouvre ou ferme l'enregistrement, il ne peut
			 * donc pas porter sur ce qui est en base pendant que l'utilisateur corrige.
			 */
			async refreshControl() {
				this.control = await this.checkInvoiceSupplierDetailsSum(
					this.invoicesupplier_id,
					this.lines.map(line => this.linePayload(line))
				)

				if(!this.control) return

				this.$set(this.balances, this.source, !!(this.control.ht && this.control.vat))

				const currency = this.currency

				this.amounts = {
					lines_ht: await this.priceFormat(this.control.lines_ht, currency, true),
					invoice_ht: await this.priceFormat(this.control.invoice_ht, currency, true),
					lines_ttc: await this.priceFormat(this.control.lines_ttc, currency, true),
					invoice_ttc: await this.priceFormat(this.control.invoice_ttc, currency, true)
				}

				const rates = []

				for(const rate of this.control.rates) {
					// La référence se lit sur la base hors taxes quand le document l'a donnée, et sur le
					// montant de taxe sinon : c'est ce que le serveur a comparé.
					const on_ht = rate.reference_ht !== null
					const reference = on_ht ? rate.reference_ht : rate.reference_vat

					rates.push({
						key: rate.rate === null ? 'null' : rate.rate,
						label: rate.rate === null ? this.$t('invoice.supplier_details_control_no_rate') : this.percentLabel(rate.rate),
						lines: await this.priceFormat(on_ht ? rate.lines_ht : rate.lines_vat, currency, true),
						reference: reference === null ? '' : await this.priceFormat(reference, currency, true),
						match: rate.match
					})
				}

				this.rates = rates
			}
		},

		mounted() {
			this.init_component()
		},

		beforeDestroy() {
			this.stopAIPolling()
		}
	}
</script>
